import { useEffect, useRef, useState } from "react";
import { Play } from "lucide-react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { useLanguage } from "@/contexts/LanguageContext";
import announcementVideo from "@/assets/ibylit-announcement.mp4.asset.json";

const AnnouncementBar = () => {
  const { t } = useLanguage();
  const copy = t.announcement;
  const visible = true;
  const [open, setOpen] = useState(false);
  const barRef = useRef<HTMLDivElement | null>(null);
  const modalVideoRef = useRef<HTMLVideoElement | null>(null);

  // Keep the fixed header and page content offset in sync with the bar height.
  useEffect(() => {
    const root = document.documentElement;
    if (!visible) {
      root.style.setProperty("--announcement-h", "0px");
      return;
    }
    const sync = () => {
      const h = barRef.current?.offsetHeight ?? 0;
      root.style.setProperty("--announcement-h", `${h}px`);
    };
    sync();
    const observer = new ResizeObserver(sync);
    if (barRef.current) observer.observe(barRef.current);
    window.addEventListener("resize", sync);
    return () => {
      observer.disconnect();
      window.removeEventListener("resize", sync);
    };
  }, [visible]);

  useEffect(() => {
    return () => {
      document.documentElement.style.setProperty("--announcement-h", "0px");
    };
  }, []);

  const handleOpenChange = (next: boolean) => {
    setOpen(next);
    if (!next && modalVideoRef.current) {
      modalVideoRef.current.pause();
      modalVideoRef.current.currentTime = 0;
    }
  };

  return (
    <>
      <div
        ref={barRef}
        className="fixed top-0 left-0 right-0 z-[60] border-b border-white/10 text-white"
        style={{
          backgroundImage:
            "radial-gradient(120% 160% at 85% 0%, hsl(214 100% 44% / 0.55) 0%, transparent 60%), linear-gradient(100deg, hsl(214 66% 10%) 0%, hsl(214 72% 16%) 100%)",
        }}
      >
        <div className="enterprise-container">
          <div className="flex items-center gap-3 py-2">
            <button
              type="button"
              onClick={() => setOpen(true)}
              className="group flex min-w-0 flex-1 items-center gap-3 rounded-lg text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-white/60"
              aria-label={copy.openVideo}
            >
              <span className="relative block h-9 w-16 shrink-0 overflow-hidden rounded-md border border-white/20 sm:h-10 sm:w-[72px]">
                <video
                  src={announcementVideo.url}
                  className="h-full w-full object-cover"
                  muted
                  loop
                  autoPlay
                  playsInline
                  preload="metadata"
                  aria-hidden="true"
                  tabIndex={-1}
                />
                <span className="absolute inset-0 flex items-center justify-center bg-[hsl(214_66%_8%_/_0.35)] transition-colors group-hover:bg-[hsl(214_66%_8%_/_0.15)]">
                  <Play className="h-3.5 w-3.5 fill-white text-white" aria-hidden="true" />
                </span>
              </span>
              <span className="min-w-0 text-xs leading-snug sm:text-sm">
                <span className="line-clamp-2 font-medium text-white/95 sm:line-clamp-1">
                  {copy.message}
                </span>
                <span className="hidden text-[11px] uppercase tracking-[0.18em] text-white/55 md:inline">
                  {copy.cta}
                </span>
              </span>
            </button>

          </div>
        </div>
      </div>

      <Dialog open={open} onOpenChange={handleOpenChange}>
        <DialogContent className="max-w-3xl border-border bg-background p-2 sm:p-3">
          <DialogTitle className="sr-only">{copy.modalTitle}</DialogTitle>
          <DialogDescription className="sr-only">{copy.message}</DialogDescription>
          {open && (
            <video
              ref={modalVideoRef}
              src={announcementVideo.url}
              className="aspect-video w-full rounded-lg bg-black"
              controls
              autoPlay
              playsInline
            />
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default AnnouncementBar;
